import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService, authService } from '../services/api';
import { toast } from 'react-toastify';
import DashboardLayout from '../components/DashboardLayout';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ComposedChart
} from 'recharts';

const PERFORMANCE_COLORS = {
  excellent: '#10B981',
  good: '#F59E0B', 
  needsImprovement: '#EF4444',
  noData: '#6B7280'
};

const DashboardContent = () => {
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar autenticación
    if (!authService.isDashboardAuthenticated()) {
      navigate('/dashboard/login');
      return;
    }

    const loadAnalytics = async () => {
      try {
        const data = await dashboardService.getAnalytics();
        setAnalytics(data);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalytics();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Error al cargar datos</h2>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Preparar datos para los gráficos
  const evaluacionesPorMesData = analytics.evaluaciones_por_mes ? Object.entries(analytics.evaluaciones_por_mes)
    .map(([mes, cantidad]) => ({
      mes: mes.substring(5), // Solo el mes
      evaluaciones: cantidad,
      evaluaciones360: (analytics.evaluaciones_360_por_mes && analytics.evaluaciones_360_por_mes[mes]) || 0,
      total: cantidad + ((analytics.evaluaciones_360_por_mes && analytics.evaluaciones_360_por_mes[mes]) || 0),
      mesCompleto: mes
    }))
    .reverse()
    .slice(-6) : []; // Últimos 6 meses

  const lideresPorPromedioData = analytics.estadisticas_por_lider
    ? analytics.estadisticas_por_lider
        .sort((a, b) => b.promedio_evaluaciones - a.promedio_evaluaciones)
        .slice(0, 10)
        .map(lider => ({
          nombre: lider.nombre_lider.split(' ').slice(0, 2).join(' '), // Nombre corto
          promedio: parseFloat(lider.promedio_evaluaciones.toFixed(2)),
          evaluaciones: lider.total_evaluaciones,
          empleados: lider.empleados_evaluados
        }))
    : [];

  // Datos para el gráfico de distribución de rendimiento
  const distribucionRendimiento = analytics.estadisticas_por_empleado 
    ? analytics.estadisticas_por_empleado.reduce((acc, empleado) => {
        const promedio = empleado.promedio_evaluaciones;
        if (promedio >= 4.5) acc.excelente++;
        else if (promedio >= 3.5) acc.bueno++;
        else if (promedio >= 2.5) acc.regular++;
        else if (promedio >= 1.0) acc.necesitaMejora++;
        else acc.sinDatos++;
        return acc;
      }, { excelente: 0, bueno: 0, regular: 0, necesitaMejora: 0, sinDatos: 0 })
    : { excelente: 0, bueno: 0, regular: 0, necesitaMejora: 0, sinDatos: 0 };

  const pieDataRendimiento = [
    { name: 'Excelente (4.5-5.0)', value: distribucionRendimiento.excelente, color: PERFORMANCE_COLORS.excellent },
    { name: 'Bueno (3.5-4.4)', value: distribucionRendimiento.bueno, color: PERFORMANCE_COLORS.good },
    { name: 'Regular (2.5-3.4)', value: distribucionRendimiento.regular, color: '#F97316' },
    { name: 'Necesita Mejora (1.0-2.4)', value: distribucionRendimiento.necesitaMejora, color: PERFORMANCE_COLORS.needsImprovement },
    { name: 'Sin Datos', value: distribucionRendimiento.sinDatos, color: PERFORMANCE_COLORS.noData }
  ].filter(item => item.value > 0);

  // Comparativa líderes vs empleados
  const comparativaData = [
    {
      categoria: 'Evaluaciones Realizadas',
      lideres: analytics.estadisticas_generales?.total_evaluaciones_regulares || 0,
      empleados: analytics.estadisticas_generales?.total_evaluaciones_360 || 0
    },
    {
      categoria: 'Promedio General',
      lideres: parseFloat((analytics.estadisticas_generales?.promedio_general || 0).toFixed(2)),
      empleados: parseFloat((analytics.estadisticas_generales?.promedio_360 || 0).toFixed(2))
    }
  ];

  // Top performers combinado
  const topPerformers = [
    ...(analytics.estadisticas_por_empleado || []).map(emp => ({
      nombre: emp.nombre_empleado,
      tipo: 'Empleado',
      promedio: emp.promedio_evaluaciones,
      evaluaciones: emp.total_evaluaciones
    })),
    ...(analytics.estadisticas_por_lider_360 || []).map(lid => ({
      nombre: lid.nombre_lider,  // Corregido: usar nombre_lider en lugar de nombre
      tipo: 'Líder',
      promedio: lid.promedio,
      evaluaciones: lid.total_evaluaciones
    }))
  ].sort((a, b) => b.promedio - a.promedio).slice(0, 8);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header con métricas principales */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard de Evaluaciones</h1>
            <p className="text-gray-600 mt-1">Análisis integral de rendimiento organizacional</p>
          </div>
          <div className="mt-4 lg:mt-0 flex items-center space-x-4">
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border">
              <span className="text-sm text-gray-500">Última actualización:</span>
              <span className="text-sm font-semibold text-gray-900 ml-1">
                {new Date().toLocaleDateString('es-ES')}
              </span>
            </div>
          </div>
        </div>

        {/* KPIs principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Evaluaciones Combinadas */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Evaluaciones</p>
                <p className="text-3xl font-bold">
                  {analytics.estadisticas_generales?.total_evaluaciones || 0}
                </p>
                <div className="flex flex-col mt-2 space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs bg-blue-400 bg-opacity-30 px-2 py-1 rounded">
                      Regulares: {analytics.estadisticas_generales?.total_evaluaciones_regulares || 0}
                    </span>
                    <span className="text-xs bg-purple-400 bg-opacity-30 px-2 py-1 rounded">
                      360°: {analytics.estadisticas_generales?.total_evaluaciones_360 || 0}
                    </span>
                  </div>
                  {analytics.estadisticas_generales?.total_evaluaciones_dinamicas > 0 && (
                    <span className="text-xs bg-indigo-400 bg-opacity-30 px-2 py-1 rounded inline-block">
                      Dinámicas: {analytics.estadisticas_generales?.total_evaluaciones_dinamicas || 0}
                    </span>
                  )}
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Promedio Global */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Promedio Global</p>
                <p className="text-3xl font-bold">
                  {analytics.estadisticas_generales?.promedio_general?.toFixed(1) || '0.0'}
                </p>
                <div className="flex items-center mt-2">
                  <div className="w-16 bg-green-400 bg-opacity-30 rounded-full h-2 mr-2">
                    <div 
                      className="bg-white h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((analytics.estadisticas_generales?.promedio_general || 0) / 5) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs">/5.0</span>
                </div>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Participación */}
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Tasa de Participación</p>
                <p className="text-3xl font-bold">
                  {analytics.porcentaje_evaluados || '0'}%
                </p>
                <p className="text-purple-100 text-sm mt-2">
                  {analytics.empleados_evaluados || 0} de {analytics.total_empleados_sistema || 0} empleados
                </p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Líderes Activos */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Líderes Activos</p>
                <p className="text-3xl font-bold">
                  {analytics.lideres_activos || analytics.estadisticas_generales?.total_lideres || 0}
                </p>
                <p className="text-orange-100 text-sm mt-2">
                  {analytics.estadisticas_por_lider_360?.length || 0} evaluados en 360°
                </p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-lg p-3">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid principal de visualizaciones */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Tendencia temporal - Spanning 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Tendencia de Evaluaciones</h3>
              <p className="text-gray-600 text-sm">Comparativa mensual de evaluaciones regulares vs 360°</p>
            </div>
            <div className="flex space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Total: {evaluacionesPorMesData.reduce((sum, item) => sum + (item.total || 0), 0)}
              </span>
            </div>
          </div>
          
          {evaluacionesPorMesData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={evaluacionesPorMesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="mes" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => [
                    value,
                    name === 'evaluaciones' ? 'Evaluaciones Regulares' : 
                    name === 'evaluaciones360' ? 'Evaluaciones 360°' : 'Total'
                  ]}
                />
                <Legend />
                <Bar 
                  dataKey="evaluaciones" 
                  fill="#3B82F6" 
                  name="Regulares"
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="evaluaciones360" 
                  fill="#8B5CF6" 
                  name="360°"
                  radius={[4, 4, 0, 0]}
                />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  name="Total"
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-80 text-gray-500">
              <div className="text-center">
                <svg className="w-16 h-16 text-gray-400 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-1">Sin datos temporales</p>
                <p className="text-sm text-gray-500">Los datos de tendencia aparecerán aquí</p>
              </div>
            </div>
          )}
        </div>

        {/* Distribución de rendimiento */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Distribución de Rendimiento</h3>
            <p className="text-gray-600 text-sm">Clasificación por niveles de desempeño</p>
          </div>
          
          {pieDataRendimiento.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieDataRendimiento}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieDataRendimiento.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => [`${value} empleados`, name]}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-80 text-gray-500">
              <div className="text-center">
                <svg className="w-12 h-12 text-gray-400 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <p className="text-sm text-gray-500">Sin datos de rendimiento</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Segunda fila de gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Comparativa Líderes vs Empleados */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Comparativa Bidireccional</h3>
            <p className="text-gray-600 text-sm">Análisis cruzado de evaluaciones</p>
          </div>
          
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={comparativaData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="categoria" 
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar 
                dataKey="lideres" 
                fill="#3B82F6" 
                name="Evaluaciones de Líderes"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="empleados" 
                fill="#8B5CF6" 
                name="Evaluaciones a Líderes"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900">Top Performers</h3>
            <p className="text-gray-600 text-sm">Mejores evaluados - líderes y empleados</p>
          </div>
          
          <div className="space-y-3">
            {topPerformers.slice(0, 6).map((performer, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                    index === 0 ? 'bg-yellow-500' :
                    index === 1 ? 'bg-gray-400' :
                    index === 2 ? 'bg-orange-600' :
                    'bg-blue-500'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{performer.nombre}</p>
                    <p className="text-sm text-gray-500">{performer.tipo}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg text-gray-900">{performer.promedio.toFixed(1)}</p>
                  <p className="text-xs text-gray-500">{performer.evaluaciones} eval.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Estadísticas por Cliente */}
      {analytics.estadisticas_por_cliente && analytics.estadisticas_por_cliente.length > 0 && (
        <div className="bg-white overflow-hidden shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Evaluaciones por Cliente
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {analytics.estadisticas_por_cliente.map((cliente, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {cliente.cliente}
                    </h4>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {cliente.total_empleados} empleados
                    </span>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Evaluados:</span>
                      <span className="font-medium text-gray-900">
                        {cliente.empleados_evaluados} ({cliente.porcentaje_evaluados}%)
                      </span>
                    </div>
                    
                    {cliente.promedio_evaluaciones > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Promedio:</span>
                        <span className="font-medium text-gray-900">
                          {cliente.promedio_evaluaciones.toFixed(1)} / 5.0
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Evaluaciones:</span>
                      <span className="font-medium text-gray-900">
                        {cliente.total_evaluaciones}
                      </span>
                    </div>
                    
                    {/* Barra de progreso de evaluación */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progreso de evaluación</span>
                        <span>{cliente.porcentaje_evaluados}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${cliente.porcentaje_evaluados}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Gráficos */}
      <div className="space-y-8 mb-8">
        {/* Gráfico de línea - Evaluaciones por mes */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Tendencia de Evaluaciones
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Evaluaciones realizadas en los últimos 6 meses
              </p>
            </div>
            <div className="mt-2 sm:mt-0 flex gap-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Regulares: {evaluacionesPorMesData.reduce((sum, item) => sum + (item.evaluaciones || 0), 0)}
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                360°: {evaluacionesPorMesData.reduce((sum, item) => sum + (item.evaluaciones360 || 0), 0)}
              </span>
            </div>
          </div>
          {evaluacionesPorMesData.length > 0 && evaluacionesPorMesData.some(item => item.evaluaciones > 0 || item.evaluaciones360 > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={evaluacionesPorMesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="mes" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#f9fafb', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="evaluaciones" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  name="Evaluaciones Regulares"
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="evaluaciones360" 
                  stroke="#8B5CF6" 
                  strokeWidth={3}
                  name="Evaluaciones 360°"
                  dot={{ fill: '#8B5CF6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#8B5CF6', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-1">Sin datos de tendencia</p>
              <p className="text-sm text-center">No hay suficientes evaluaciones para mostrar la tendencia</p>
            </div>
          )}
        </div>

        {/* Gráfico de barras - Promedio por líder */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Rendimiento por Líder
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Promedio de evaluaciones por líder (Top 10)
              </p>
            </div>
            <div className="mt-2 sm:mt-0">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {lideresPorPromedioData.length} líderes activos
              </span>
            </div>
          </div>
          {lideresPorPromedioData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={lideresPorPromedioData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="nombre" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80}
                  tick={{ fontSize: 10 }}
                  interval={0}
                />
                <YAxis 
                  domain={[0, 5]} 
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Promedio', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#f9fafb', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  formatter={(value, name) => [
                    `${value.toFixed(2)}/5.0`,
                    'Promedio'
                  ]}
                  labelFormatter={(label) => `Líder: ${label}`}
                />
                <Legend />
                <Bar 
                  dataKey="promedio" 
                  fill="#10B981" 
                  name="Promedio"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-1">Sin datos de líderes</p>
              <p className="text-sm text-center">No hay líderes con evaluaciones completadas</p>
            </div>
          )}
        </div>
      </div>

      {/* Tablas de estadísticas */}
      <div className="space-y-8">
        {/* Estadísticas de Líderes (Evaluaciones 360°) */}
        {analytics.estadisticas_por_lider_360 && analytics.estadisticas_por_lider_360.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
                Estadísticas de Líderes - Evaluaciones 360°
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Evaluaciones de empleados hacia sus líderes
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Líder
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Evaluaciones Recibidas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Puntuación Promedio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.estadisticas_por_lider_360.map((lider, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lider.nombre_lider}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lider.total_evaluaciones}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          lider.promedio >= 4.5 ? 'bg-green-100 text-green-800' :
                          lider.promedio >= 3.5 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {lider.promedio ? lider.promedio.toFixed(2) : 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          lider.promedio >= 4.5 ? 'bg-green-100 text-green-800' :
                          lider.promedio >= 3.5 ? 'bg-yellow-100 text-yellow-800' :
                          lider.promedio >= 1 ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {lider.promedio >= 4.5 ? 'Excelente' :
                           lider.promedio >= 3.5 ? 'Bueno' :
                           lider.promedio >= 1 ? 'Necesita Mejora' :
                           'Sin Evaluaciones'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* NUEVO: Estadísticas Evaluaciones Dinámicas - Líderes */}
        {analytics.estadisticas_dinamicas_lideres && analytics.estadisticas_dinamicas_lideres.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <span className="w-3 h-3 bg-indigo-500 rounded-full mr-2"></span>
                Evaluaciones Dinámicas - Líderes
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Evaluaciones de empleados hacia líderes (Sistema Dinámico)
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Líder
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Evaluadores
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Respuestas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promedio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Última Evaluación
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.estadisticas_dinamicas_lideres.map((lider, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lider.nombre_lider}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lider.total_evaluadores}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lider.total_respuestas}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          lider.promedio_puntuacion >= 4.0 ? 'bg-green-100 text-green-800' :
                          lider.promedio_puntuacion >= 3.0 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {lider.promedio_puntuacion.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lider.ultima_evaluacion ? new Date(lider.ultima_evaluacion).toLocaleDateString('es-CO') : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* NUEVO: Estadísticas Evaluaciones Dinámicas - Empleados */}
        {analytics.estadisticas_dinamicas_empleados && analytics.estadisticas_dinamicas_empleados.length > 0 && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <span className="w-3 h-3 bg-teal-500 rounded-full mr-2"></span>
                Evaluaciones Dinámicas - Empleados
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Evaluaciones de líderes hacia empleados (Sistema Dinámico)
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empleado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cargo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Evaluadores
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Respuestas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promedio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Última Evaluación
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.estadisticas_dinamicas_empleados.map((empleado, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {empleado.nombre_empleado}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {empleado.cargo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {empleado.total_evaluadores}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {empleado.total_respuestas}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          empleado.promedio_puntuacion >= 4.0 ? 'bg-green-100 text-green-800' :
                          empleado.promedio_puntuacion >= 3.0 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {empleado.promedio_puntuacion.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {empleado.ultima_evaluacion ? new Date(empleado.ultima_evaluacion).toLocaleDateString('es-CO') : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Estadísticas por líder (Evaluaciones Regulares) */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
              Estadísticas por Líder - Evaluaciones Regulares
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Evaluaciones de líderes hacia empleados
            </p>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Líder
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Evaluaciones
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empleados
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promedio
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.estadisticas_por_lider.length > 0 ? (
                    analytics.estadisticas_por_lider.map((lider) => (
                      <tr key={lider.lider_id} className="hover:bg-gray-50">
                        <td className="px-4 sm:px-6 py-4 text-sm font-medium text-gray-900">
                          <div className="truncate max-w-xs" title={lider.nombre_lider}>
                            {lider.nombre_lider}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {lider.total_evaluaciones}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {lider.empleados_evaluados}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-900">
                              {lider.promedio_evaluaciones.toFixed(2)}
                            </span>
                            <span className="text-xs text-gray-500 ml-1">/5.0</span>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 sm:px-6 py-8 text-center text-sm text-gray-500">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-lg font-medium text-gray-900 mb-1">No hay evaluaciones disponibles</p>
                          <p className="text-sm text-gray-500">Los líderes aún no han creado evaluaciones</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Estadísticas por empleado */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Top Empleados por Rendimiento
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Empleados con mejores promedios de evaluación
            </p>
          </div>
          <div className="overflow-x-auto">
            <div className="max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empleado
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cargo
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Evaluaciones
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promedio
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.estadisticas_por_empleado.length > 0 ? (
                    analytics.estadisticas_por_empleado
                      .sort((a, b) => b.promedio_evaluaciones - a.promedio_evaluaciones)
                      .slice(0, 15)
                      .map((empleado) => (
                        <tr key={empleado.empleado_id} className="hover:bg-gray-50">
                          <td className="px-4 sm:px-6 py-4 text-sm font-medium text-gray-900">
                            <div className="truncate max-w-xs" title={empleado.nombre_empleado}>
                              {empleado.nombre_empleado}
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-4 text-sm text-gray-500">
                            <div className="truncate max-w-xs" title={empleado.cargo}>
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                                {empleado.cargo}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {empleado.total_evaluaciones}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <div className="flex items-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {empleado.promedio_evaluaciones.toFixed(2)}
                                </span>
                                <span className="text-xs text-gray-500 ml-1">/5.0</span>
                              </div>
                              <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full"
                                  style={{ width: `${(empleado.promedio_evaluaciones / 5) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 sm:px-6 py-8 text-center text-sm text-gray-500">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <p className="text-lg font-medium text-gray-900 mb-1">No hay empleados evaluados</p>
                          <p className="text-sm text-gray-500">Aún no se han realizado evaluaciones</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Resumen ejecutivo */}
      <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900">Resumen Ejecutivo</h3>
          <p className="text-gray-600 text-sm">Métricas avanzadas de análisis organizacional</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Métrica combinada de eficiencia */}
          <div className="text-center p-4 border border-gray-200 rounded-lg bg-gradient-to-b from-blue-50 to-blue-100">
            <div className="text-2xl font-bold text-blue-600">
              {(((analytics.estadisticas_generales?.total_evaluaciones_regulares || 0) + 
                 (analytics.estadisticas_generales?.total_evaluaciones_360 || 0)) / 
                Math.max((analytics.total_empleados_sistema || 1) + (analytics.total_lideres_sistema || 1), 1) * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-700 mt-1 font-medium">Índice de Actividad</div>
            <div className="text-xs text-gray-500">Evaluaciones vs Personal Total</div>
          </div>

          {/* Gap de retroalimentación */}
          <div className="text-center p-4 border border-gray-200 rounded-lg bg-gradient-to-b from-purple-50 to-purple-100">
            <div className="text-2xl font-bold text-purple-600">
              {Math.abs((analytics.estadisticas_generales?.total_evaluaciones_regulares || 0) - 
                       (analytics.estadisticas_generales?.total_evaluaciones_360 || 0))}
            </div>
            <div className="text-sm text-gray-700 mt-1 font-medium">Gap Bidireccional</div>
            <div className="text-xs text-gray-500">Diferencia entre tipos</div>
          </div>

          {/* Cobertura de liderazgo */}
          <div className="text-center p-4 border border-gray-200 rounded-lg bg-gradient-to-b from-green-50 to-green-100">
            <div className="text-2xl font-bold text-green-600">
              {(((analytics.estadisticas_por_lider_360?.length || 0) / Math.max(analytics.total_lideres_sistema || 1, 1)) * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-700 mt-1 font-medium">Cobertura 360°</div>
            <div className="text-xs text-gray-500">Líderes evaluados</div>
          </div>

          {/* Tendencia mensual */}
          <div className="text-center p-4 border border-gray-200 rounded-lg bg-gradient-to-b from-orange-50 to-orange-100">
            <div className="text-2xl font-bold text-orange-600">
              {evaluacionesPorMesData.length > 1 ? 
                ((evaluacionesPorMesData[evaluacionesPorMesData.length - 1]?.total || 0) - 
                 (evaluacionesPorMesData[evaluacionesPorMesData.length - 2]?.total || 0) > 0 ? '+' : '') +
                ((evaluacionesPorMesData[evaluacionesPorMesData.length - 1]?.total || 0) - 
                 (evaluacionesPorMesData[evaluacionesPorMesData.length - 2]?.total || 0))
                : '0'
              }
            </div>
            <div className="text-sm text-gray-700 mt-1 font-medium">Tendencia Mensual</div>
            <div className="text-xs text-gray-500">Variación último mes</div>
          </div>
        </div>

        {/* Comentarios Recientes */}
        {analytics.comentarios_recientes && analytics.comentarios_recientes.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-lg font-bold text-gray-900 mb-4">💬 Comentarios de Evaluaciones</h4>
            <div className="space-y-4">
              {analytics.comentarios_recientes.slice(0, 10).map((comentario, index) => (
                <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">{comentario.evaluador_nombre}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-semibold text-gray-900">{comentario.evaluado_nombre}</span>
                        {comentario.tipo_evaluacion === 'empleado_lider' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            360°
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        {comentario.evaluado_cargo && `${comentario.evaluado_cargo} • `}
                        {new Date(comentario.fecha).toLocaleDateString('es-ES', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${i < comentario.puntuacion ? 'text-yellow-400' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                      <span className="text-sm font-semibold text-gray-700 ml-1">{comentario.puntuacion}/5</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 italic mb-2">
                    "{comentario.pregunta}"
                  </div>
                  <div className="text-sm text-gray-800 bg-white p-3 rounded border border-gray-200">
                    {comentario.comentario}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Métricas adicionales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-200">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">
              {analytics.estadisticas_por_empleado?.filter(emp => emp.promedio_evaluaciones >= 4.0).length || 0}
            </div>
            <div className="text-sm text-gray-600">Alto Rendimiento (≥4.0)</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">
              {analytics.estadisticas_por_lider_360?.filter(lid => lid.promedio >= 4.0).length || 0}
            </div>
            <div className="text-sm text-gray-600">Líderes Destacados (≥4.0)</div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">
              {evaluacionesPorMesData.reduce((sum, item) => sum + (item.total || 0), 0)}
            </div>
            <div className="text-sm text-gray-600">Total Últimos 6 Meses</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  return (
    <DashboardLayout>
      <DashboardContent />
    </DashboardLayout>
  );
};

export default Dashboard;